import { faCircleStar } from '@fortawesome/pro-solid-svg-icons/faCircleStar';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { createInvoiceThunk, InvoicesStatus } from '@spike/invoices-action';
import { Order } from '@spike/order-model';
import { OrdersStatus, saveOrderRedeemsThunk } from '@spike/orders-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { Button } from 'components/UI';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getAllOrderItems } from '../utils/OrderUtils';
import { css } from '@emotion/react';
import Loader from './Loader';
import styles from './MembershipsButtons.module.css';

export interface MembershipButtonsProps {
    order: Order;
    bookingId?: number;
    onClickButton?: () => void;
    className?: string;
}

const override = css`
    display: block;
    margin: 0 auto;
    border-color: red;
`;

export const MembershipsButtons: FunctionComponent<MembershipButtonsProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);
    const hasMemberships = useMemo(
        () => getAllOrderItems(props.order).some(orderItem => orderItem.redeem !== undefined),
        [props.order]
    );

    const [saving, setSaving] = useState(false);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.SaveOrderRedeemSuccess:
                dispatch(createInvoiceThunk(apiClientWrapper, props.order.id, props.bookingId));
                break;
            case OrdersStatus.Error:
                setSaving(false);
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.CreateInvoiceSuccess:
            case InvoicesStatus.Error:
                setSaving(false);
        }
    }, [invoicesStatus]);

    const label = hasMemberships ? 'Edit Membership' : 'Add Membership';

    const removeAllMemberships = () => {
        setSaving(true);

        const orderItemIds = getAllOrderItems(props.order)
            .map(orderItem => orderItem.id)
            .filter(id => id !== undefined);

        const redeems = orderItemIds.map(itemId => ({
            orderItemId: itemId,
            redeem: undefined
        }));

        dispatch(saveOrderRedeemsThunk(apiClientWrapper, props.order.id, redeems));
    };

    const spinner = (
        <div className={styles.spinnerContainer}>
            <Loader
                loading={saving}
                color="black"
                size={5}
            />
        </div>
    );

    return (
        <div className={clsx(styles.container, props.className)}>
            <Button
                label={label}
                startIcon={faCircleStar}
                onClick={props.onClickButton}
                disabled={saving}
            />
            {hasMemberships && (
                <Box
                    onClick={removeAllMemberships}
                    className={styles.linkContainer}
                >
                    {saving ? spinner : <span className={styles.link}>Remove Memberships</span>}
                </Box>
            )}
        </div>
    );
};

export default MembershipsButtons;
